// The clip editor's maths, kept apart from the canvas so it can be tested
// without one. Every position is in CLIP BEATS, the frame warp markers and
// the start/end/loop markers share, or in FILE SECONDS. Pixels appear only
// in `ClipView`.
//
// Live's gestures, as the editor does them:
//   - dragging a marker changes its BEAT. The file second stays put, so the
//     audio either side stretches to meet it.
//   - Shift-dragging changes its FILE SECOND. The beat stays put, so the
//     audio slides under the marker.
//   - double-click adds a marker exactly where the map already is, which
//     changes nothing until it is dragged.

import { beatAtFileSeconds, fileSecondsAt, type WarpMarker } from "@web-shared/lib/warp";

/** The closest two markers may sit, in beats and in file seconds. */
export const MIN_MARKER_BEATS = 1 / 64;
export const MIN_MARKER_SEC = 0.001;

/** The markers the editor edits: the clip's own when it has a map (two
 * or more). Otherwise the straight line its source tempo already plays
 * (file start and file end), so the first marker added bends nothing. */
export function effectiveMarkers(
  markers: readonly WarpMarker[] | undefined,
  fileSec: number,
  sourceBpm: number,
): WarpMarker[] {
  if (markers && markers.length >= 2) return markers.map((m) => ({ beat: m.beat, sec: m.sec }));
  const beatsPerSec = sourceBpm > 0 ? sourceBpm / 60 : 2;
  const end = Math.max(MIN_MARKER_SEC, fileSec);
  return [
    { beat: 0, sec: 0 },
    { beat: end * beatsPerSec, sec: end },
  ];
}

/** The beat a marker may be dragged to: between its neighbours. */
export function clampBeat(markers: readonly WarpMarker[], index: number, beat: number): number {
  const lo = index > 0 ? markers[index - 1]!.beat + MIN_MARKER_BEATS : Number.NEGATIVE_INFINITY;
  const hi = index < markers.length - 1 ? markers[index + 1]!.beat - MIN_MARKER_BEATS : Number.POSITIVE_INFINITY;
  return Math.min(hi, Math.max(lo, beat));
}

/** Drag marker `index` to `beat` (clamped): its audio stretches to meet it. */
export function moveMarkerBeat(markers: readonly WarpMarker[], index: number, beat: number): WarpMarker[] {
  const out = markers.map((m) => ({ ...m }));
  const m = out[index];
  if (!m) return out;
  m.beat = clampBeat(markers, index, beat);
  return out;
}

/** Shift-drag marker `index` to file second `sec` (clamped between its
 * neighbours and ≥ 0): the audio slides under a beat that stays put. */
export function moveMarkerSec(markers: readonly WarpMarker[], index: number, sec: number): WarpMarker[] {
  const out = markers.map((m) => ({ ...m }));
  const m = out[index];
  if (!m) return out;
  const lo = index > 0 ? markers[index - 1]!.sec + MIN_MARKER_SEC : 0;
  const hi = index < markers.length - 1 ? markers[index + 1]!.sec - MIN_MARKER_SEC : Number.POSITIVE_INFINITY;
  m.sec = Math.min(hi, Math.max(lo, sec));
  return out;
}

/**
 * A marker where the map already puts `sec` of the file, inserted in order.
 * The index it landed at comes back with it. Null when it would sit on top
 * of an existing marker.
 */
export function insertMarkerAtSec(
  markers: readonly WarpMarker[],
  sec: number,
  fallbackBpm: number,
): { markers: WarpMarker[]; index: number } | null {
  if (!(sec >= 0)) return null;
  const beat = beatAtFileSeconds(markers, sec, fallbackBpm);
  if (markers.some((m) => Math.abs(m.beat - beat) < MIN_MARKER_BEATS || Math.abs(m.sec - sec) < MIN_MARKER_SEC)) return null;
  const out = [...markers.map((m) => ({ ...m })), { beat, sec }].sort((a, b) => a.beat - b.beat);
  return { markers: out, index: out.findIndex((m) => m.beat === beat) };
}

/** The same, at a beat of the current map. */
export function insertMarkerAtBeat(
  markers: readonly WarpMarker[],
  beat: number,
  fallbackBpm: number,
): { markers: WarpMarker[]; index: number } | null {
  return insertMarkerAtSec(markers, fileSecondsAt(markers, beat, fallbackBpm), fallbackBpm);
}

/** Remove marker `index`. The map keeps at least two, since fewer is no map. */
export function removeMarker(markers: readonly WarpMarker[], index: number): WarpMarker[] | null {
  if (markers.length <= 2 || index < 0 || index >= markers.length) return null;
  return markers.filter((_, i) => i !== index).map((m) => ({ ...m }));
}

/** Live's "Warp From Here (Straight)": every marker after `index` goes, and
 * the segment ending at `index` carries its tempo to the end of the file. */
export function warpStraightFrom(markers: readonly WarpMarker[], index: number): WarpMarker[] | null {
  if (index < 1 || index >= markers.length - 1) return null;
  return markers.slice(0, index + 1).map((m) => ({ ...m }));
}

/** The tempo of the segment right of marker `index`, or of the last
 * segment for the last marker. Null for a degenerate segment. */
export function segmentBpm(markers: readonly WarpMarker[], index: number): number | null {
  const i = Math.min(index, markers.length - 2);
  const a = markers[i];
  const b = markers[i + 1];
  if (!a || !b || !(b.sec - a.sec > 0)) return null;
  return ((b.beat - a.beat) / (b.sec - a.sec)) * 60;
}

/** The nearest transient to `sec` within `withinSec`, if any. */
export function nearestTransient(transientsSec: readonly number[], sec: number, withinSec: number): number | null {
  let best: number | null = null;
  let dist = withinSec;
  for (const t of transientsSec) {
    const d = Math.abs(t - sec);
    if (d <= dist) {
      dist = d;
      best = t;
    }
  }
  return best;
}

/** The marker whose beat is within `withinBeats` of `beat`, nearest first. */
export function markerNear(markers: readonly WarpMarker[], beat: number, withinBeats: number): number | null {
  let best: number | null = null;
  let dist = withinBeats;
  markers.forEach((m, i) => {
    const d = Math.abs(m.beat - beat);
    if (d <= dist) {
      dist = d;
      best = i;
    }
  });
  return best;
}

/** Round to the grid (0 = free). */
export function snapBeat(beat: number, grid: number): number {
  return grid > 0 ? Math.round(beat / grid) * grid : beat;
}

/** The visible window: beats from `startBeat`, `pxPerBeat` wide. */
export interface ClipView {
  startBeat: number;
  pxPerBeat: number;
}

export const beatToX = (v: ClipView, beat: number): number => (beat - v.startBeat) * v.pxPerBeat;
export const xToBeat = (v: ClipView, x: number): number => v.startBeat + x / v.pxPerBeat;

/** Zoom by `factor` about the beat under `x`, within sane bounds. */
export function zoomAt(v: ClipView, x: number, factor: number): ClipView {
  const pxPerBeat = Math.min(4000, Math.max(2, v.pxPerBeat * factor));
  const anchor = xToBeat(v, x);
  return { pxPerBeat, startBeat: anchor - x / pxPerBeat };
}

/** A grid fine enough to be useful at this zoom and coarse enough to read:
 * the largest power-of-two fraction of a beat (or multiple of a bar) whose
 * lines sit at least `minPx` apart. */
export function gridFor(pxPerBeat: number, minPx = 12): number {
  let grid = 1 / 16;
  while (grid * pxPerBeat < minPx && grid < 64) grid *= 2;
  return grid;
}

/**
 * Warp, as the editor offers it: off, or one of Live's six modes by name
 * (the wire carries the value, `WARP_MODES` in lib/edits). Beats, Re-Pitch,
 * Complex and Complex Pro are real in the browser's wasm engine; Tones and
 * Texture play as Complex here and the hint says so — they are still
 * offered because the value syncs to Live, which plays the real thing.
 */
export type WarpChoice = "off" | "beats" | "tones" | "texture" | "repitch" | "complex" | "pro";

/** Live's modes in the editor's order, with the wire value of each. */
export const WARP_CHOICES: ReadonlyArray<{ choice: Exclude<WarpChoice, "off">; value: number; label: string }> = [
  { choice: "beats", value: 0, label: "Beats" },
  { choice: "tones", value: 1, label: "Tones" },
  { choice: "texture", value: 2, label: "Texture" },
  { choice: "repitch", value: 3, label: "Re-Pitch" },
  { choice: "complex", value: 4, label: "Complex" },
  { choice: "pro", value: 6, label: "Complex Pro" },
];

const REPITCH = 3;
const COMPLEX = 4;

/** The choice a clip's warp state shows; an unknown mode value reads as Complex. */
export function warpChoiceOf(isWarped: boolean, warpModeValue: number): WarpChoice {
  if (!isWarped) return "off";
  return WARP_CHOICES.find((m) => m.value === warpModeValue)?.choice ?? "complex";
}

export function warpFor(choice: WarpChoice): { isWarped: boolean; warpModeValue?: number } {
  if (choice === "off") return { isWarped: false };
  return { isWarped: true, warpModeValue: WARP_CHOICES.find((m) => m.choice === choice)?.value ?? COMPLEX };
}

/** Warp on, keeping the clip's own mode when it keeps pitch — a clip from
 * Live that says "Beats" is not rewritten to "Complex" by warping it on;
 * Re-Pitch becomes Complex, since "on" from a loop or marker edit means
 * "follow the tempo at this pitch". */
export function warpOnKeeping(currentMode: number): { isWarped: boolean; warpModeValue?: number } {
  return { isWarped: true, warpModeValue: currentMode === REPITCH ? COMPLEX : currentMode };
}

/**
 * The "+ Marker" button: a marker at `beat` — on the nearest transient
 * within `snapSec` of it when there is one, since a marker is almost always
 * wanted on a hit — or, when that spot is taken, nowhere.
 */
export function addMarkerNear(
  markers: readonly WarpMarker[],
  beat: number,
  transientsSec: readonly number[],
  snapSec: number,
  fallbackBpm: number,
): { markers: WarpMarker[]; index: number } | null {
  const sec = fileSecondsAt(markers, beat, fallbackBpm);
  const hit = nearestTransient(transientsSec, sec, snapSec);
  return hit !== null
    ? (insertMarkerAtSec(markers, hit, fallbackBpm) ?? insertMarkerAtBeat(markers, beat, fallbackBpm))
    : insertMarkerAtBeat(markers, beat, fallbackBpm);
}

/** Two fingers: zoom by how far apart they moved, about where they began,
 * and follow their midpoint as it moves (pan while pinching). */
export function pinchView(start: ClipView, startMid: number, startDist: number, mid: number, dist: number): ClipView {
  const zoomed = zoomAt(start, startMid, Math.max(0.05, dist) / Math.max(0.05, startDist));
  return { ...zoomed, startBeat: zoomed.startBeat - (mid - startMid) / zoomed.pxPerBeat };
}
